import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { format } from "date-fns";
import { Download, Calendar, Clock, Archive, Loader2, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { supabase } from "@/integrations/supabase/client";

export default function Takeout() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const { execute: invoke } = useEdgeFunction({ auth: true });
  const queryClient = useQueryClient();
  
  // Enable keyboard shortcuts for back navigation
  useKeyboardNavigation({
    onBack: () => navigate("/dashboard"),
  });
  
  const [formSchedule, setFormSchedule] = useState<any>(null);

  // Fetch takeout requests
  const { data: requests = [], isLoading: isLoadingRequests } = useQuery({
    queryKey: ['takeoutRequests', session?.user.id],
    queryFn: async () => {
      if (!session) return [];
      const { data } = await supabase
        .from('takeout_requests')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);
      return data || [];
    },
    enabled: !!session,
  });

  // Fetch takeout schedule
  const { data: initialSchedule, isLoading: isLoadingSchedule } = useQuery({
    queryKey: ['takeoutSchedule', session?.user.id],
    queryFn: async () => {
      if (!session) return null;
      const { data } = await invoke('manage-takeout-schedule', {
        body: { action: 'get' }
      });
      return data?.schedule || null;
    },
    enabled: !!session,
  });

  // Initialize form schedule from query data
  useEffect(() => {
    if (initialSchedule) {
      setFormSchedule(initialSchedule);
    }
  }, [initialSchedule]);

  const loading = isLoadingRequests || isLoadingSchedule;

  // Request takeout mutation
  const requestTakeoutMutation = useMutation({
    mutationFn: () => invoke('request-takeout', { showErrorToast: true }),
    onSuccess: () => {
      toast.success('Takeout requested successfully! We\'ll email you when it\'s ready.');
      queryClient.invalidateQueries({ queryKey: ['takeoutRequests'] });
    },
  });

  // Save schedule mutation
  const saveScheduleMutation = useMutation({
    mutationFn: (scheduleToSave: any) => {
      if (!scheduleToSave) throw new Error("Schedule data is missing.");
      return invoke('manage-takeout-schedule', {
        body: { action: 'upsert', schedule: scheduleToSave },
        showErrorToast: true,
      });
    },
    onSuccess: () => {
      toast.success('Schedule saved successfully');
      queryClient.invalidateQueries({ queryKey: ['takeoutSchedule'] });
    },
  });

  // Delete schedule mutation
  const deleteScheduleMutation = useMutation({
    mutationFn: () => invoke('manage-takeout-schedule', {
      body: { action: 'delete' },
      showErrorToast: true,
    }),
    onSuccess: () => {
      setFormSchedule(null);
      toast.success('Schedule deleted');
      queryClient.invalidateQueries({ queryKey: ['takeoutSchedule'] });
    },
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'processing':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container max-w-4xl py-8">
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
        <h1 className="text-3xl font-bold mb-2">Data Takeout</h1>
        <p className="text-muted-foreground">
          Export all your data in a portable format. Never feel locked in.
        </p>
      </div>

      <div className="grid gap-6">
        {/* Request Takeout */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5" />
              Request New Takeout
            </CardTitle>
            <CardDescription>
              Export all your workspaces, boards, projects, sessions, and memories. 
              Includes AI-ready markdown files for use with ChatGPT, Claude, and other assistants.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => requestTakeoutMutation.mutate()} disabled={requestTakeoutMutation.isPending}>
              {requestTakeoutMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Request Takeout
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Scheduled Takeouts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Automated Takeouts
            </CardTitle>
            <CardDescription>
              Set up automatic exports on a weekly or monthly schedule
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="schedule-enabled">Enable automated takeouts</Label>
              <Switch
                id="schedule-enabled"
                checked={formSchedule?.enabled || false}
                onCheckedChange={(checked) => {
                  if (!formSchedule) {
                    setFormSchedule({
                      frequency: 'weekly',
                      day_of_week: 1,
                      time: '09:00:00',
                      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                      enabled: checked
                    });
                  } else {
                    setFormSchedule({ ...formSchedule, enabled: checked });
                  }
                }}
              />
            </div>

            {(formSchedule?.enabled || formSchedule) && (
              <>
                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <Select
                    value={formSchedule?.frequency || 'weekly'}
                    onValueChange={(value) =>
                      setFormSchedule({ ...formSchedule, frequency: value, day_of_week: value === 'weekly' ? 1 : null, day_of_month: value === 'monthly' ? 1 : null })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formSchedule?.frequency === 'weekly' && (
                  <div className="space-y-2">
                    <Label>Day of Week</Label>
                    <Select
                      value={formSchedule?.day_of_week?.toString() || '1'}
                      onValueChange={(value) =>
                        setFormSchedule({ ...formSchedule, day_of_week: parseInt(value) })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Sunday</SelectItem>
                        <SelectItem value="1">Monday</SelectItem>
                        <SelectItem value="2">Tuesday</SelectItem>
                        <SelectItem value="3">Wednesday</SelectItem>
                        <SelectItem value="4">Thursday</SelectItem>
                        <SelectItem value="5">Friday</SelectItem>
                        <SelectItem value="6">Saturday</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {formSchedule?.frequency === 'monthly' && (
                  <div className="space-y-2">
                    <Label>Day of Month</Label>
                    <Select
                      value={formSchedule?.day_of_month?.toString() || '1'}
                      onValueChange={(value) =>
                        setFormSchedule({ ...formSchedule, day_of_month: parseInt(value) })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                          <SelectItem key={day} value={day.toString()}>
                            {day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button onClick={() => saveScheduleMutation.mutate(formSchedule)} disabled={saveScheduleMutation.isPending}>
                    {saveScheduleMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Schedule'
                    )}
                  </Button>
                  {formSchedule?.id && (
                    <Button variant="outline" onClick={() => deleteScheduleMutation.mutate()} disabled={deleteScheduleMutation.isPending}>
                      {deleteScheduleMutation.isPending ? 'Deleting...' : 'Delete Schedule'}
                    </Button>
                  )}
                </div>

                {formSchedule?.next_run_at && formSchedule.enabled && (
                  <p className="text-sm text-muted-foreground">
                    Next takeout: {format(new Date(formSchedule.next_run_at), 'PPpp')}
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Recent Takeouts */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Takeouts</CardTitle>
            <CardDescription>Your past data exports</CardDescription>
          </CardHeader>
          <CardContent>
            {requests.length === 0 ? (
              <p className="text-sm text-muted-foreground">No takeouts yet</p>
            ) : (
              <div className="space-y-3">
                {requests.map((request) => (
                  <div
                    key={request.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(request.status)}
                      <div>
                        <p className="font-medium capitalize">{request.status}</p>
                        <p className="text-sm text-muted-foreground">
                          Requested {format(new Date(request.requested_at), 'PPp')}
                        </p>
                        {request.file_size && (
                          <p className="text-sm text-muted-foreground">
                            {(request.file_size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                        )}
                      </div>
                    </div>
                    {request.status === 'completed' && request.download_url && (
                      <div className="flex flex-col items-end gap-1">
                        <a
                          href={request.download_url}
                          download={`takeout-${format(new Date(request.completed_at), 'yyyy-MM-dd')}.zip`}
                          className="inline-flex"
                        >
                          <Button size="sm">
                            <Download className="mr-2 h-4 w-4" />
                            Download
                          </Button>
                        </a>
                        {request.expires_at && (
                          <p className="text-xs text-muted-foreground">
                            Expires {format(new Date(request.expires_at), 'PP')}
                          </p>
                        )}
                      </div>
                    )}
                    {request.status === 'failed' && request.error_message && (
                      <p className="text-sm text-red-500">{request.error_message}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-muted/50">
          <CardHeader>
            <CardTitle className="text-lg">What's included?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Complete JSON export</p>
                <p className="text-muted-foreground">All your data in machine-readable format</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">AI-ready markdown files</p>
                <p className="text-muted-foreground">
                  Upload personas to ChatGPT, Claude, or Gemini for instant advisory sessions
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Organized structure</p>
                <p className="text-muted-foreground">
                  Individual persona files and combined board files for flexible use
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Full chat history</p>
                <p className="text-muted-foreground">All your sessions and conversations</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
